import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { SelectionPlugin } from '@atlaskit/editor-plugin-selection/selection-plugin-type';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import { getTopLevelSignature, type TopLevelNode } from './signature';

export type HubTemplateLockPlugin = NextEditorPlugin<
	'hubTemplateLock',
	{ dependencies: [SelectionPlugin] }
>;

const signatureOf = (doc: PMNode): string => {
	const nodes: TopLevelNode[] = [];
	doc.forEach((node) => {
		nodes.push(node);
	});
	return getTopLevelSignature(nodes);
};

/**
 * Freezes the structure of a hub page: which elements are on it, and in what
 * order. Content edits still apply, so the config panel and the breakout
 * controls keep working — only add, delete and reorder are refused.
 *
 * This is the enforcement layer for topic pages, whose template is fixed for
 * every customer. Hiding the add / reorder / delete affordances (the components
 * panel, the drop target, quick insert, drag handles, the extension toolbar's
 * copy and delete buttons) keeps admins from reaching for something that will
 * not work; this plugin is what makes it true regardless of which affordance
 * was missed — keyboard delete, paste, or one added later.
 *
 * Add it only to a locked page's preset: it takes no options, so its presence
 * *is* the lock.
 */
export const hubTemplateLockPlugin: HubTemplateLockPlugin = ({ api }) => ({
	name: 'hubTemplateLock',

	pmPlugins() {
		return [
			{
				name: 'hubTemplateLock',
				plugin: () =>
					new SafePlugin({
						view: () => {
							let destroyed = false;

							// Defer the command until ProseMirror has completed plugin-view setup.
							queueMicrotask(() => {
								if (!destroyed) {
									api?.core.actions.execute(api?.selection?.commands.hideCursor(true));
								}
							});

							return {
								destroy: () => {
									destroyed = true;
									api?.core.actions.execute(api?.selection?.commands.hideCursor(false));
								},
							};
						},
						// The whole top-level sequence is compared, not just the extension
						// nodes, so a locked page cannot grow stray paragraphs either.
						filterTransaction: (tr, state) =>
							!tr.docChanged || signatureOf(tr.doc) === signatureOf(state.doc),
					}),
			},
		];
	},
});
